import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Email as EmailIcon,
  Shield as ShieldIcon,
  Verified as VerifiedIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'react-toastify';

const profileSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
});

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[a-z]/, 'Must contain lowercase letter')
    .matches(/[A-Z]/, 'Must contain uppercase letter')
    .matches(/[0-9]/, 'Must contain number')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const Account: React.FC = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleProfileSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      await updateProfile(values);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (values: any, { setSubmitting, resetForm }: any) => {
    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      toast.success('Password changed successfully');
      resetForm();
    } catch (error: any) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Account Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Manage your account information and security settings
      </Typography>

      <Paper sx={{ mt: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<PersonIcon />} label="Profile" iconPosition="start" />
          <Tab icon={<SecurityIcon />} label="Security" iconPosition="start" />
        </Tabs>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>

            <Formik
              initialValues={{
                firstName: user.firstName || '',
                lastName: user.lastName || '',
              }}
              validationSchema={profileSchema}
              onSubmit={handleProfileSubmit}
              enableReinitialize
            >
              {({ errors, touched, isSubmitting, dirty }) => (
                <Form>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Field name="firstName">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="First Name *"
                            required
                            error={touched.firstName && Boolean(errors.firstName)}
                            helperText={touched.firstName && errors.firstName}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field name="lastName">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Last Name *"
                            required
                            error={touched.lastName && Boolean(errors.lastName)}
                            helperText={touched.lastName && errors.lastName}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        value={user.email}
                        disabled
                        helperText="Email cannot be changed"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={isSubmitting || !dirty}
                      >
                        Save Changes
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Account Status
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Email Verification"
                  secondary={
                    user.emailVerified
                      ? 'Your email is verified'
                      : 'Your email is not verified'
                  }
                />
                {user.emailVerified ? (
                  <Chip label="Verified" color="success" icon={<VerifiedIcon />} size="small" />
                ) : (
                  <Chip label="Not Verified" color="warning" icon={<CancelIcon />} size="small" />
                )}
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <ShieldIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Multi-Factor Authentication"
                  secondary={
                    user.mfaEnabled
                      ? 'MFA is enabled for extra security'
                      : 'MFA is not enabled'
                  }
                />
                {user.mfaEnabled ? (
                  <Chip label="Enabled" color="success" size="small" />
                ) : (
                  <Chip label="Disabled" color="default" size="small" />
                )}
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Account Status"
                  secondary={user.isActive ? 'Your account is active' : 'Account disabled'}
                />
                {user.isActive ? (
                  <Chip label="Active" color="success" size="small" />
                ) : (
                  <Chip label="Disabled" color="error" size="small" />
                )}
              </ListItem>
            </List>
          </Box>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>

            <Formik
              initialValues={{
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
              }}
              validationSchema={passwordSchema}
              onSubmit={handlePasswordSubmit}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form>
                  <Grid container spacing={3} sx={{ mt: 1, maxWidth: 600 }}>
                    <Grid item xs={12}>
                      <Field name="currentPassword">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Current Password *"
                            type={showPasswords.current ? 'text' : 'password'}
                            required
                            error={touched.currentPassword && Boolean(errors.currentPassword)}
                            helperText={touched.currentPassword && errors.currentPassword}
                            autoComplete="current-password"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowPasswords((prev) => ({
                                        ...prev,
                                        current: !prev.current,
                                      }))
                                    }
                                    edge="end"
                                    aria-label="Toggle current password visibility"
                                  >
                                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Field name="newPassword">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="New Password *"
                            type={showPasswords.new ? 'text' : 'password'}
                            required
                            error={touched.newPassword && Boolean(errors.newPassword)}
                            helperText={touched.newPassword && errors.newPassword}
                            autoComplete="new-password"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowPasswords((prev) => ({ ...prev, new: !prev.new }))
                                    }
                                    edge="end"
                                    aria-label="Toggle new password visibility"
                                  >
                                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Field name="confirmPassword">
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Confirm New Password *"
                            type={showPasswords.confirm ? 'text' : 'password'}
                            required
                            error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                            helperText={touched.confirmPassword && errors.confirmPassword}
                            autoComplete="new-password"
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() =>
                                      setShowPasswords((prev) => ({
                                        ...prev,
                                        confirm: !prev.confirm,
                                      }))
                                    }
                                    edge="end"
                                    aria-label="Toggle confirm password visibility"
                                  >
                                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Password must be at least 8 characters and contain uppercase, lowercase,
                        and numbers.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SecurityIcon />}
                        disabled={isSubmitting}
                      >
                        Change Password
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>
              Roles & Permissions
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your roles:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {user.roles.map((role) => (
                  <Chip key={role} label={role} color="primary" variant="outlined" />
                ))}
              </Box>
            </Box>

            {user.allowedJurisdictions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Allowed jurisdictions:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {user.allowedJurisdictions.map((jurisdiction) => (
                    <Chip key={jurisdiction} label={jurisdiction} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {user.allowedCompanyIds.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info">
                  Your access is restricted to {user.allowedCompanyIds.length} specific{' '}
                  {user.allowedCompanyIds.length === 1 ? 'company' : 'companies'}.
                </Alert>
              </Box>
            )}
          </Box>
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default Account;
